import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { DoczHandle } from "@norkron/web-docz";
import {
  type AnchorSelector,
  DEFAULT_TOOLBAR_CONFIG,
  type EditorHostBridge,
} from "@norkron/web-elaborate-editor";
import { api } from "../api";
import {
  type Kind,
  type TickAction,
  type VersionTick,
  WorkbenchStage,
  SideBySideDiff,
  buildDiffPermalink,
  parseDiffLink,
  serializeDiffLink,
} from "@norkron/web-review-workbench";
import {
  articleApi,
  type ResourceRole,
  type ResourceSavePayload,
  type Section,
  type SectionLink,
  type WorkbenchComment,
} from "../api/articleUnits";
import type {
  AnnotationDraft,
  CommentCreate,
  CommentStatus,
  CommentType,
  VersionRestoreReport,
  VisibilityChange,
  WorkspaceMember,
} from "../api/types";
import { ApiError } from "../api/types";
import { toggleOwnReaction } from "../realtime/reconcile";
import { getRestOutbox } from "../sync/restOutbox";
import { mintTempId } from "../sync/tempIds";
import { useAuthOptional } from "../auth/AuthContext";
import { CommentComposer } from "../components/review/CommentComposer";
import {
  RestoreConfirm,
  RestoreReportCard,
} from "../components/VersionRestore";
import { WorkbenchBar } from "../components/workbench/WorkbenchBar";
import { ResourcePane } from "../components/workbench/ResourcePane";
import { ResourceHeader } from "../components/workbench/ResourceHeader";
import { CommentGutter } from "../components/workbench/CommentGutter";
import { ResearchColumn } from "../components/workbench/ResearchColumn";
import { SectionHandleLayer } from "../components/workbench/SectionHandleLayer";
import { PaneBottomBar } from "../components/workbench/PaneBottomBar";
import { CompareTray } from "../components/workbench/CompareTray";
import type { SavingEditorHandle } from "../components/canvas/CanvasElaborateEditor";
import { DoczCanvas } from "../components/canvas/DoczCanvas";
import { VersionCanvas } from "../components/canvas/VersionCanvas";
import { paperLayoutMode } from "../components/canvas/paperLayoutMode";

import { revealElement, type ScrollIntent } from "../lib/scrollMotion";
import { useReadCanvasSurface } from "../components/canvas/useReadCanvasSurface";
import { buildDoczHandle } from "../lib/doczHandle";
import { hashQuote } from "../lib/anchoring";
import { mergeSurfaces } from "../lib/reviewOrder";
import { commentTargetFromSurface, selectorFromDraft } from "../lib/surfaceComment";
import { countWordsInHtml, stripHtml } from "../lib/wordCount";
import { EmptyState, Spinner } from "../compose/states";
import { Button } from "../compose/controls";
import { ArrowLeft, FileText } from "../compose/Icon";
import { useToast } from "../compose/Toast";
import { useAsync } from "../lib/useAsync";
import { useVersionCompare } from "../lib/useVersionCompare";
import {
  diffNoteAnchorsFromComments,
  resolveThreadRoot,
} from "../lib/diffNoteAnchors";
import {
  counterpartLabel,
  parseShow,
  serializeShow,
} from "../lib/workbenchLayout";

type Ticks = Record<Kind, VersionTick[]>;
const EMPTY_TICKS: Ticks = { brief: [], article: [], research: [] };

/** A past (non-head) resource version the reviewer opened from the timeline:
 *  its seq + content hash (for the pane header chip) and its archived body.
 *  `null` for a pane ⇒ the head.
 *
 *  `html` is NO LONGER PAINTED — owner decision D-E moved the body onto the canvas
 *  engine (`<VersionCanvas>`, which fetches that revision's block-DTO bytes). It
 *  stays because the bottom bar's reader-visible WORD COUNT is measured from the
 *  shown body's HTML, and counting words in markup is not rendering it.
 *
 *  `versionNum` is the numeric `el.doc_versions.id` the canvas transport needs.
 *  Derived ONCE at open time rather than re-parsed at every render, so the pane
 *  and the fetch can never disagree about which revision is on screen. */
type ViewVer = {
  versionId: string;
  versionNum: number;
  seq: number;
  hash: string;
  html: string;
  label: string;
};
const NO_VIEW_VER: Record<Kind, ViewVer | null> = {
  brief: null,
  article: null,
  research: null,
};

/** The root-comment authoring popover (§2.5 dual-gutter authoring): a durable
 *  selector captured from a docz PANE's text selection, positioned at the
 *  selection rect, tagged with the pane's `role` so the created comment posts to
 *  THAT resource's document and lands in its gutter. Mirrors DocumentViewer's
 *  popover, generalised to the multi-pane workbench. */
interface PopoverState {
  x: number;
  y: number;
  draft: AnnotationDraft;
  role: "brief" | "article";
  /** Present when the draft came from a LIVE EDITING SURFACE (`buildSelector()`).
   *  Handed back to `handle.resolveAnchor(selector, id)` once the server mints
   *  the comment id, so the highlight band paints on the surface immediately —
   *  on the canvas there is no server-rendered read pane to re-anchor against. */
  selector?: AnchorSelector;
  /** Shown INSIDE the composer when the selection cannot be anchored durably (a
   *  table / list / blank range). The comment is still authorable — this is a
   *  disclosure, not a refusal, and it deliberately is NOT a toast: a toast that
   *  replaces the popup is what made the reviewer think the button was dead. */
  notice?: string;
}

/**
 * `<ArticleWorkbench>` — the Unified Article Workbench at
 * `/w/:slug/article/:unitId` (design §2). The unit of work is an ARTICLE UNIT
 * = { brief, research, article }; the screen is a single runtime-derived
 * multi-pane stage driven by three toggle pills (`<PillToggleGroup>`), with
 * comment gutters interleaved between panes and a floating per-resource version
 * timeline — all composed from `@norkron/web-review-workbench` primitives.
 *
 * DEFAULT shown-set on open = Article ONLY (persisted per-unit in the URL
 * `?show=`). The two docz panes (brief/article) paint through the shared
 * `useDoczCanvas` engine — the SAME implementation the legacy DocumentViewer
 * uses; research renders through `<ResearchColumn>` (the two-editor boundary).
 */
export function ArticleWorkbench() {
  const { slug = "", unitId = "" } = useParams<{ slug: string; unitId: string }>();
  const toast = useToast();
  const auth = useAuthOptional();
  const user = auth?.user ?? null;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: unit, loading, error } = useAsync(
    () => articleApi.getArticleUnit(slug, unitId),
    [slug, unitId],
  );

  // The workspace's persisted editor-toolbar config (EditorToolbarConfig) — drives
  // the v0.5.0 <ToolbarShell> mounted over each docz editor. Falls back to the
  // canonical default while it loads / if the workspace never set one, so the
  // toolbar always renders.
  const { data: loadedToolbarCfg } = useAsync(
    () => api.getEditorToolbar(slug),
    [slug],
  );
  const toolbarCfg = loadedToolbarCfg ?? DEFAULT_TOOLBAR_CONFIG;

  const shown = useMemo(
    () => parseShow(searchParams.get("show")),
    [searchParams],
  );
  const setShown = useCallback(
    (next: Kind[]) => {
      const params = new URLSearchParams(searchParams);
      params.set("show", serializeShow(next.length > 0 ? next : ["article"]));
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const [comments, setComments] = useState<WorkbenchComment[]>([]);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  // §2.5 authoring: the open root-comment composer popover (null = none).
  const [popover, setPopover] = useState<PopoverState | null>(null);
  // rich-comments §13: the comment whose card lifts (a doc highlight hovered).
  const [linkedCommentId, setLinkedCommentId] = useState<number | null>(null);
  const [ticks, setTicks] = useState<Ticks>(EMPTY_TICKS);
  // Per-pane "viewing a past version" state (null ⇒ the live head). Set when the
  // reviewer clicks a non-head tick on the timeline; the pane then shows that
  // version's server-rendered snapshot read-only + the header chip reflects it.
  const [viewVer, setViewVer] =
    useState<Record<Kind, ViewVer | null>>(NO_VIEW_VER);
  const [links, setLinks] = useState<SectionLink[]>([]);
  const [researchMd, setResearchMd] = useState("");
  // Bottom-bar target word count N (design §4 · N1). Seeded from the unit's
  // server-stated `targetWords`; the word-count popup can override it locally
  // (a persist terminal is a follow-up — the read/surface path is server-authoritative).
  const [targetWords, setTargetWords] = useState<number | undefined>(undefined);
  // A tick bumped on every editor change so the read-derived word count refreshes
  // live while editing (the editor handle's toHtml() is re-read on each bump).
  const [editTick, setEditTick] = useState(0);
  const [editing, setEditing] = useState<Record<Kind, boolean>>({
    brief: false,
    article: false,
    research: false,
  });
  // Has the unit's review data (comments / members / version streams / links)
  // finished loading? That effect ENDS by resetting `editing` to all-false, so
  // anything that wants to put a pane INTO edit must wait for this — see the
  // `?edit=` deep-link effect.
  const [detailLoaded, setDetailLoaded] = useState(false);
  // W2 edit-mode: per-docz-pane live handle + a dirty/saving flag per pane.
  const [handles, setHandles] = useState<{
    brief: DoczHandle | null;
    article: DoczHandle | null;
  }>({ brief: null, article: null });
  const [dirty, setDirty] = useState<Record<Kind, boolean>>({
    brief: false,
    article: false,
    research: false,
  });
  const [saving, setSaving] = useState<Record<Kind, boolean>>({
    brief: false,
    article: false,
    research: false,
  });
  // `SavingEditorHandle` — the shared editor contract PLUS the optional save
  // refusal predicate the serializing canvas surface answers (see `onSave`).
  const briefEditorRef = useRef<SavingEditorHandle>(null);
  const articleEditorRef = useRef<SavingEditorHandle>(null);
  // ── POST-SAVE READ COHERENCE (P3-FIX-4B) ──────────────────────────────────
  // `unit` is `useAsync` data fetched ONCE per unitId, so nothing refreshed it
  // after a save: Save posted the new body, dropped the pane back to read mode,
  // and the read surface went on painting `unit.resources[kind].rendered_html`
  // — the body from BEFORE the edit. The reviewer's own save appeared to have
  // done nothing, and a second reader on the same version saw different text.
  //
  // The fix is NOT a re-derivation: the bytes the server accepted are exactly
  // the bytes we posted, so the accepted body is adopted as the pane's read
  // body until the next full load. Refetching the whole unit instead would
  // blank `useAsync`'s data and flash the screen back to a spinner (losing the
  // reviewer's pane set, scroll and open threads) for a string we already hold.
  const [savedBody, setSavedBody] = useState<Partial<Record<Kind, string>>>({});
  // Bumped on every accepted save so the CANVAS reader re-fetches its transport
  // (it owns its own fetch under a stable docId — see `<ReadCanvas revision>`).
  const [readRev, setReadRev] = useState(0);
  // The canvas readers (P3-8), so a comment deep-link can scroll the surface the
  // reviewer is actually looking at rather than the hidden DOM copy.
  // W4 (§2.7): pick-any-two version compare — timeline pin-A/pin-B, the compare
  // tray, and the resulting <SideBySideDiff> model. All state + the fetch live in
  // the hook (composing the existing compareResource/compareSection terminals);
  // this screen only wires the gestures + renders the tray.
  const compare = useVersionCompare(unitId, ticks, toast.show);
  const stageRef = useRef<HTMLDivElement>(null);
  // FIX-1 (toolbar placement): the per-pane header dock the editor PORTALS its
  // formatting toolbar into (design: the toolbar is header chrome, an extension of
  // `.wb-pane__head` ABOVE the paper — never a sticky element inside `.wb-paper`).
  // The dock <div> lives in the pane header; its element is captured via a callback
  // ref into state so, once it mounts, DoczCanvas → ElaborateEditor re-renders with
  // a real `toolbarContainer` and portals the toolbar into it. Held in state (not a
  // plain ref) precisely so that mount triggers that re-render.
  const [dockEl, setDockEl] = useState<{
    brief: HTMLElement | null;
    article: HTMLElement | null;
  }>({ brief: null, article: null });
  const setBriefDock = useCallback((el: HTMLDivElement | null) => {
    setDockEl((d) => (d.brief === el ? d : { ...d, brief: el }));
  }, []);
  const setArticleDock = useCallback((el: HTMLDivElement | null) => {
    setDockEl((d) => (d.article === el ? d : { ...d, article: el }));
  }, []);

  // §2.5 authoring: a text selection in a docz PANE opens the root-comment
  // composer at the selection rect, tagged with that pane's resource role so the
  // created comment posts to the pane's document (→ its gutter). One stable
  // handler per docz pane keeps `useDoczCanvas`'s `onMouseUp` reference stable.
  const openComposer = useCallback(
    (role: "brief" | "article", draft: AnnotationDraft | null, rect: DOMRect | null) => {
      if (!draft || !rect) {
        setPopover(null);
        return;
      }
      setPopover({ x: rect.left + rect.width / 2, y: rect.bottom + 8, draft, role });
    },
    [],
  );
  const onSelectBrief = useCallback(
    (d: AnnotationDraft | null, r: DOMRect | null) => openComposer("brief", d, r),
    [openComposer],
  );
  const onSelectArticle = useCallback(
    (d: AnnotationDraft | null, r: DOMRect | null) => openComposer("article", d, r),
    [openComposer],
  );

  // Load the review data + per-resource version streams once the unit lands.
  useEffect(() => {
    if (!unit) return;
    let live = true;
    // Re-arm for THIS unit: an in-SPA hop to another project must load its own
    // detail before its own `?edit=` is honoured.
    setDetailLoaded(false);
    void (async () => {
      const [cs, ms, bv, av, rv, lk] = await Promise.all([
        articleApi.listComments(unit.id),
        articleApi.listMembers(unit.id),
        articleApi.resourceVersions(unit.id, "brief"),
        articleApi.resourceVersions(unit.id, "article"),
        articleApi.resourceVersions(unit.id, "research"),
        articleApi.listLinks(unit.id).catch(() => [] as SectionLink[]),
      ]);
      if (!live) return;
      setComments(cs);
      setMembers(ms);
      setTicks({ brief: bv, article: av, research: rv });
      setLinks(lk);
      setResearchMd(unit.resources.research.body_md ?? "");
      setTargetWords(unit.targetWords);
      // A fresh unit shows every pane at its head (no past snapshot open).
      setViewVer(NO_VIEW_VER);
      // A fresh unit starts read-only with no dirty edits / stale handles.
      setEditing({ brief: false, article: false, research: false });
      setDirty({ brief: false, article: false, research: false });
      setHandles({ brief: null, article: null });
      // A freshly LOADED unit carries the server's body, so the local
      // accepted-save override has nothing left to say. Dropping it here is what
      // keeps the override from outliving the fetch that supersedes it.
      setSavedBody({});
      // THE UNIT IS NOW FULLY LOADED. The `?edit=` deep-link waits on this
      // signal and MUST NOT be started before it: this effect ends by resetting
      // `editing` to all-false, so an auto-edit that won the race against these
      // six requests was silently wiped a moment later. That is a second,
      // independent cause of "the param was consumed and the pane stayed
      // read-only" — and being a race it presented as intermittent: the same
      // scenario opened the editor on one run and not on the next.
      setDetailLoaded(true);
    })();
    return () => {
      live = false;
    };
  }, [unit]);

  // MEMOIZE the per-pane homed comment slices: a fresh `filter()` each render
  // would change useDoczCanvas's `comments` dep every render → its anchor effect
  // would setState on every render → infinite re-render. Stable references break
  // that loop (mirrors DocumentViewer, where `comments` is a stable state value).
  const briefHomed = useMemo(
    () => comments.filter((c) => c.home_resource === "brief"),
    [comments],
  );
  const articleHomed = useMemo(
    () => comments.filter((c) => c.home_resource === "article"),
    [comments],
  );

  // THE ONE BODY every read surface in this screen paints: the body the server
  // accepted on the last save if there was one, else the loaded body. Read
  // through this — never `unit.resources[kind].rendered_html` directly — or the
  // stale-read defect comes back on whichever surface skipped it.
  const bodyFor = useCallback(
    (kind: "brief" | "article"): string | undefined =>
      savedBody[kind] ?? unit?.resources[kind].rendered_html,
    [savedBody, unit],
  );

  // The two READ SURFACES (brief + article), canvas only. `useDoczCanvas` — which
  // painted each pane's `rendered_html` into a hidden `.ela-doc` div and
  // re-anchored the comment set by walking its DOM text nodes — is deleted.
  // `<ReadCanvas>` resolves anchors from the docz BLOCKS and paints the bands;
  // these hooks hold the two maps the review rail renders from and expose the
  // imperative facade (`focusAnchor`, the pending band, the card→span glow).
  const briefCanvas = useReadCanvasSurface();
  const articleCanvas = useReadCanvasSurface();

  // RELOAD RE-ANCHOR ON THE EDITING SURFACE. `useDoczCanvas` re-anchors the READ
  // pane only (`active: … && !editing[kind]`). Nothing re-anchored the EDITING
  // surface, so a comment authored on the paginated canvas came back after a
  // reload as a gutter card over UNMARKED text — the durable server row was
  // intact but nothing on the page pointed at the quote. Hand every homed
  // comment's stored selector to the mounted surface; it re-locates the quote in
  // the live block text and paints its band (or reports `orphaned`, honestly).
  // Depends on the edit-mode change tick so a relayout re-paints the bands.
  useEffect(() => {
    for (const kind of ["brief", "article"] as const) {
      if (!editing[kind]) continue;
      const handle = (kind === "brief" ? briefEditorRef : articleEditorRef).current;
      if (!handle) continue;
      for (const c of kind === "brief" ? briefHomed : articleHomed) {
        handle.resolveAnchor(selectorFromDraft(c), String(c.id));
      }
    }
  }, [editing, briefHomed, articleHomed, editTick]);

  // rich-comments §3.2/§13: pending-highlight + card→span hover fan-outs across
  // BOTH docz panes (the pending lives in the pane its selection was made in; a
  // card→span brighten is a no-op in the pane that doesn't hold that id).
  const onComposerType = useCallback(
    (t: CommentType) => {
      if (!popover) return;
      (popover.role === "brief" ? briefCanvas : articleCanvas).setPendingType(t);
    },
    [popover, briefCanvas, articleCanvas],
  );
  const clearPending = useCallback(() => {
    briefCanvas.clearPending();
    articleCanvas.clearPending();
  }, [briefCanvas, articleCanvas]);
  const onHoverCard = useCallback(
    (id: number | null) => {
      briefCanvas.setLinkedHover(id);
      articleCanvas.setLinkedHover(id);
    },
    [briefCanvas, articleCanvas],
  );

  const anchorStates = useMemo(
    () => ({ ...briefCanvas.anchorStates, ...articleCanvas.anchorStates }),
    [briefCanvas.anchorStates, articleCanvas.anchorStates],
  );

  // The gutter's document order. The panes are merged in the workbench's
  // canonical left→right order (`KIND_ORDER`), so a brief-homed note and an
  // article-homed note that both live in block 0 cannot collide — the PANE
  // ranks first. A pane with no docz surface at all (research is markdown) and
  // a pane in edit mode publish nothing, and their notes fall back to their
  // recorded anchor rather than piling into the unanchored group.
  const documentOrder = useMemo(
    () => ({
      points: mergeSurfaces([
        { kind: "brief", points: briefCanvas.anchorPoints },
        { kind: "article", points: articleCanvas.anchorPoints },
      ]),
      states: anchorStates,
    }),
    [briefCanvas.anchorPoints, articleCanvas.anchorPoints, anchorStates],
  );

  // The stage scrolls HORIZONTALLY between panes. Every caller below is a
  // reviewer act — a counterpart chip, a section link, a timeline tick, an
  // article chip — so the default intent is `navigate`: the travel is what says
  // "the thing you asked for is over there", and it collapses to an instant jump
  // under `prefers-reduced-motion`. A deep-link landing passes `"restore"`.
  const scrollPaneIntoView = useCallback(
    (kind: Kind, intent: ScrollIntent = "navigate") => {
      const el = stageRef.current?.querySelector(
        `[data-testid="resource-pane-${kind}"]`,
      );
      if (el) revealElement(el, intent, { block: "nearest", inline: "center" });
    },
    [],
  );

  // Timeline "view a version" (plain tick click / Enter): swap the pane to the
  // selected version. The HEAD tick returns to the live head canvas; a past tick
  // fetches that version's server-rendered snapshot (`getVersion`, the endpoint
  // DocumentViewer already drives) and shows it read-only, with the header chip +
  // hash reflecting it. research (markdown, no rendered_html snapshot) just scrolls.
  // ── REVERT TO A VERSION (right-click a timeline tick) ─────────────────────
  // `git revert`, never `git reset`: the server appends a NEW revision holding
  // the chosen one's content and leaves everything in between alone. The whole
  // write is the EXISTING restore terminal, so the capability gate, the
  // pre-revert backup, the destructive-clear/live-markup refusals and the
  // comment-anchor rebase all apply unchanged — this screen adds an affordance,
  // not a second write path.
  const [revertAsk, setRevertAsk] = useState<{
    kind: ResourceRole;
    versionId: string;
    label: string;
  } | null>(null);
  const [reverting, setReverting] = useState(false);
  const [revertReport, setRevertReport] = useState<VersionRestoreReport | null>(
    null,
  );

  const doRevert = useCallback(async () => {
    if (!unit || !revertAsk) return;
    const { kind, versionId, label } = revertAsk;
    setReverting(true);
    try {
      const report = await articleApi.revertResource(
        unit.id,
        kind,
        versionId,
        `revert to ${label}`,
      );
      setRevertReport(report);
      setRevertAsk(null);
      // The pane must repaint from the SERVER, not from what it had loaded: the
      // revert changed the head under a docId that did not change, so nothing
      // else would tell the canvas reader to re-fetch.
      setReadRev((r) => r + 1);
      setViewVer((v) => ({ ...v, [kind]: null }));
      const nextTicks = await articleApi.resourceVersions(unit.id, kind);
      setTicks((t) => ({ ...t, [kind]: nextTicks }));
      // NEVER claim a revert that did not happen. The server answers
      // `bytes_written: false` when the stored content already matched, and
      // reporting that as success is the exact dishonesty the restore report
      // exists to prevent.
      toast.show(
        report.bytes_written
          ? `Reverted ${kind} to ${label} — saved as a new version`
          : `Nothing changed — ${kind} already held ${label}'s content`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        toast.show("You don't have permission to revert versions");
      } else if (err instanceof ApiError && err.status === 404) {
        toast.show("That version is no longer available");
      } else if (err instanceof ApiError && err.status === 409) {
        // The server REFUSED with a reason (an empty source version would wipe
        // a document that has a body; or the archived markup can execute when
        // painted). Show ITS words — a generic failure message is how an
        // operator retries the write that was just refused for a good reason.
        toast.show(err.message);
      } else {
        toast.show("Couldn't revert to that version");
      }
    } finally {
      setReverting(false);
    }
  }, [unit, revertAsk, toast]);

  const onViewVersion = useCallback(
    async (kind: Kind, versionId: string) => {
      if (!unit) return;
      const tick = ticks[kind].find((t) => t.versionId === versionId);
      if (!tick) return;
      if (tick.isHead) {
        setViewVer((v) => (v[kind] ? { ...v, [kind]: null } : v));
        scrollPaneIntoView(kind);
        return;
      }
      if (kind === "research") {
        scrollPaneIntoView(kind);
        return;
      }
      try {
        const detail = await api.getVersion(
          unit.resources[kind].doc_id,
          Number(versionId),
        );
        setViewVer((v) => ({
          ...v,
          [kind]: {
            versionId,
            // The id the SERVER acknowledged, not the one the tick guessed: the
            // canvas transport is keyed on it, and a pane painting one revision
            // while its header names another is invisible on a canvas.
            versionNum: detail.id,
            seq: tick.seq,
            hash: detail.content_sha256,
            html: detail.rendered_html ?? "",
            label: tick.label,
          },
        }));
        // A read-only snapshot can't be edited — drop edit mode if it was on.
        setEditing((e) => (e[kind] ? { ...e, [kind]: false } : e));
        scrollPaneIntoView(kind);
      } catch {
        toast.show("Couldn't open that version");
      }
    },
    [unit, ticks, scrollPaneIntoView, toast],
  );

  // Return a pane from a past-version snapshot to the live head.
  const returnToHead = useCallback((kind: Kind) => {
    setViewVer((v) => (v[kind] ? { ...v, [kind]: null } : v));
  }, []);

  // `intent` is the ONE thing that separates a reviewer clicking a note card's
  // anchor button (animated travel to the quote) from a `?comment=` permalink
  // LANDING (instant — nobody is watching the viewport move, and animating it
  // races the browser's own restoration). Same code path, one classified input.
  const focusComment = useCallback(
    (commentId: number, intent: ScrollIntent = "navigate") => {
      const c = comments.find((x) => x.id === commentId);
      if (!c) return;
      const home = c.home_resource;
      if (!shown.includes(home)) {
        setShown([...shown, home]);
        toast.show(`Showing ${home}`);
        return;
      }
      let state = null as null | string;
      // ONE PAINTER. There is no DOM copy to fall back to any more, so a jump the
      // canvas cannot place is a MISS and is reported as one — the fallback used
      // to hide exactly that, scrolling a hidden div and reporting success while
      // nothing moved on screen.
      if (home === "brief") state = briefCanvas.focusAnchor(commentId, intent);
      else if (home === "article")
        state = articleCanvas.focusAnchor(commentId, intent);
      else {
        scrollPaneIntoView("research", intent);
        state = "exact";
      }
      if (state === null) {
        toast.show("This comment's text is no longer in the document");
      }
    },
    [comments, shown, setShown, toast, briefCanvas, articleCanvas, scrollPaneIntoView],
  );

  // ── Diff deep-links + in-header version switching + diff-note anchors ──────────
  // (design §2 / §4 / §5). The `?diff=<token>` param round-trips the OPEN comparison
  // (scope + A/B + the revealed change) exactly like `?show=` round-trips the shown
  // set: on landing we OPEN the encoded compare (fetch A→B) and reveal its change; an
  // in-header version switch RECOMPARES in place and REWRITES the token; a copy-link
  // button serializes a shareable permalink; `?comment=&reply=` reveal a diff note on
  // its change. The two lib deps are the pure token codec (`parseDiffLink` /
  // `serializeDiffLink` / `buildDiffPermalink`) + `<SideBySideDiff>`'s reveal/switch
  // props — no bespoke diff chrome (RULE ZERO / design §5.7).
  const { openCompareFromToken, compareCtx, recompare, closeDiff } = compare;
  const rawDiff = searchParams.get("diff") ?? "";
  const diffToken = useMemo(() => parseDiffLink(rawDiff), [rawDiff]);
  const commentParam = useMemo(() => {
    const raw = searchParams.get("comment");
    const n = raw == null ? NaN : Number(raw);
    return Number.isInteger(n) && n > 0 ? n : null;
  }, [searchParams]);
  // `revealHash` drives the ONE-SHOT scroll+bloom the lib does on landing (stable so
  // manual nav does not re-bloom); `activeChangeHash` is the change the copy-link +
  // `?diff=` currently point at (updated as the reviewer navigates hunks).
  const [revealHash, setRevealHash] = useState<string | undefined>(undefined);
  const [activeChangeHash, setActiveChangeHash] = useState<string | undefined>(
    undefined,
  );
  // The `?diff=` token string the OPEN compare already reflects — the shared guard
  // that stops the landing (URL→state) + sync (state→URL) effects from ping-ponging.
  const reconciledDiff = useRef<string | null>(null);
  const openingDiff = useRef(false);

  // Landing: open the compare a `?diff=<token>` encodes. `openCompareFromToken`
  // returns false until the resource's version list is in hand, so this retries as
  // `ticks` load (the callback's identity changes with `ticks`).
  useEffect(() => {
    if (rawDiff === "" || diffToken === null) return;
    if (rawDiff === reconciledDiff.current) return;
    if (openingDiff.current) return;
    openingDiff.current = true;
    let cancelled = false;
    void (async () => {
      const ok = await openCompareFromToken(diffToken);
      if (!cancelled && ok) {
        reconciledDiff.current = rawDiff;
        setRevealHash(diffToken.changeHash);
        setActiveChangeHash(diffToken.changeHash);
      }
      openingDiff.current = false;
    })();
    return () => {
      cancelled = true;
      openingDiff.current = false;
    };
  }, [rawDiff, diffToken, openCompareFromToken]);

  // Sync: whenever the open comparison (or the active change) changes, re-serialize
  // the `?diff=` token into the URL (replace) so a SWITCHED comparison is itself a
  // shareable permalink. The reconciled-token guard keeps it from fighting landing.
  useEffect(() => {
    if (compareCtx === null) return;
    const serialized = serializeDiffLink({
      scope: compareCtx.scope,
      aVersionId: compareCtx.aId,
      bVersionId: compareCtx.bId,
      ...(activeChangeHash ? { changeHash: activeChangeHash } : {}),
    });
    if (serialized === reconciledDiff.current && rawDiff === serialized) return;
    reconciledDiff.current = serialized;
    if (rawDiff !== serialized) {
      const params = new URLSearchParams(searchParams);
      params.set("diff", serialized);
      setSearchParams(params, { replace: true });
    }
  }, [compareCtx, activeChangeHash, rawDiff, searchParams, setSearchParams]);

  // The header copy-link permalink for the OPEN comparison (design §2.5).
  const diffPermalink = useMemo(() => {
    if (compareCtx === null || typeof window === "undefined") return undefined;
    return buildDiffPermalink(window.location.origin, window.location.pathname, {
      scope: compareCtx.scope,
      aVersionId: compareCtx.aId,
      bVersionId: compareCtx.bId,
      ...(activeChangeHash ? { changeHash: activeChangeHash } : {}),
    });
  }, [compareCtx, activeChangeHash]);

  // Diff-note anchors from the EXISTING comment read (design §3–§4 render seam), and
  // the `?comment=` reveal target resolved to the thread ROOT the badge sits on.
  const diffNoteAnchors = useMemo(
    () => diffNoteAnchorsFromComments(comments),
    [comments],
  );
  const revealNoteCommentId = useMemo(() => {
    if (diffToken === null || commentParam === null) return undefined;
    return String(resolveThreadRoot(comments, commentParam));
  }, [diffToken, commentParam, comments]);

  // ── Ticket / permalink deep-link → reveal the note in the review rail ─────────
  // A bare `?comment=<id>` (a ticket click, or a copy-link permalink WITHOUT a
  // `?diff=` — that diff-note case is owned above by `revealNoteCommentId`) lands
  // here: ensure the note's home pane is shown, then ONE-SHOT (a) reuse the doc
  // jump (`focusComment` → scroll + bloom the anchored span), (b) scroll the rail
  // CARD into view, and (c) flash-highlight it for 3s via the STATE-driven
  // `flashCommentId` (the card renders the `comment-card--reveal` class from it —
  // imperatively toggling the class fails, React re-renders clobber it). The
  // thread also auto-expands when the id targets a reply (`revealCommentId` prop).
  // Fires once per landed id; a NEW deep-link re-arms it.
  const [flashCommentId, setFlashCommentId] = useState<number | null>(null);
  const revealedCommentRef = useRef<number | null>(null);
  // `focusComment` is called from the deferred reveal via a ref, NOT a dep: its
  // identity changes when a pane re-anchors (deps include the live canvas objects),
  // and as a dep that re-render would re-run the effect + cancel the in-flight RAF.
  const focusCommentRef = useRef(focusComment);
  focusCommentRef.current = focusComment;
  useEffect(() => {
    if (commentParam === null) {
      revealedCommentRef.current = null;
      return;
    }
    if (diffToken !== null) return; // the diff overlay owns this reveal
    if (comments.length === 0) return;
    if (revealedCommentRef.current === commentParam) return;
    const rootId = resolveThreadRoot(comments, commentParam);
    const root = comments.find((c) => c.id === rootId);
    if (!root) return;
    // Ensure the note's home pane is shown so its gutter card + doc span exist;
    // bail this pass and let the effect re-run once `shown` updates + it mounts.
    const home = root.home_resource;
    if (!shown.includes(home)) {
      setShown([...shown, home]);
      return;
    }
    // Retry across frames until the freshly-shown gutter card has painted, then
    // fire the reveal ONCE. `flashCommentId` is NOT an effect dep, so the flash
    // state update never re-runs this effect; the fire-once guard is set only on
    // success, so StrictMode's dev mount→unmount→mount just retries.
    let cancelled = false;
    let raf = 0;
    let attempts = 0;
    const tryReveal = () => {
      if (cancelled) return;
      const stage = stageRef.current;
      const card = stage?.querySelector<HTMLElement>(
        `.comment-card[data-comment-id="${rootId}"]`,
      );
      if (card) {
        revealedCommentRef.current = commentParam;
        setFlashCommentId(rootId); // state-driven 3s highlight (see clear effect)
        // A LANDING, not a navigation: jump, do not travel (lib/scrollMotion).
        revealElement(card, "restore", { block: "center" });
        focusCommentRef.current(rootId, "restore"); // doc-span reveal + bloom
        return;
      }
      if (attempts++ < 90) raf = requestAnimationFrame(tryReveal);
    };
    raf = requestAnimationFrame(tryReveal);
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [commentParam, diffToken, comments, shown, setShown]);

  // Auto-clear the one-shot flash after the 3s fade completes. Keyed on
  // `flashCommentId` so it arms exactly when a flash starts; StrictMode's
  // double-invoke just reschedules → the highlight still clears once.
  useEffect(() => {
    if (flashCommentId === null) return;
    const t = window.setTimeout(() => setFlashCommentId(null), 3000);
    return () => window.clearTimeout(t);
  }, [flashCommentId]);

  // ── DOCUMENT → RAIL: a click on commented text opens its thread ───────────────
  // The reverse of `focusComment` (rail → document), and the DOM affordance the
  // canvas lost with `de16828`: every `.docz-comment` span carried
  // `cursor: pointer` and clicking it revealed its card. `<ReadCanvas>` resolves
  // the click to a comment id (it owns the hit-test and paints that span in its
  // GLOW); this is the host half — reveal the card and run the SAME 3s
  // `comment-card--reveal` flash the deep-link landing uses, so a comment reached
  // from the text and a comment reached from a permalink look identical.
  //
  // A `navigate`, not a `restore`: the reviewer clicked and is watching the rail
  // move (`lib/scrollMotion`).
  const revealCommentCard = useCallback(
    (commentId: number | null) => {
      if (commentId === null) return;
      const rootId = resolveThreadRoot(comments, commentId);
      const card = stageRef.current?.querySelector<HTMLElement>(
        `.comment-card[data-comment-id="${rootId}"]`,
      );
      if (!card) return;
      revealElement(card, "navigate", { block: "center" });
      setFlashCommentId(rootId);
    },
    [comments],
  );

  // In-header version switch → recompare in place for the new pair; clear the
  // revealed change (may not exist in the new pair). The sync effect rewrites `?diff=`.
  const onDiffCompare = useCallback(
    (aId: string, bId: string) => {
      setRevealHash(undefined);
      setActiveChangeHash(undefined);
      void recompare(aId, bId);
    },
    [recompare],
  );

  // Close the diff overlay AND scrub `?diff=&comment=&reply=` from the URL.
  const onDiffClose = useCallback(() => {
    closeDiff();
    reconciledDiff.current = null;
    setRevealHash(undefined);
    setActiveChangeHash(undefined);
    const params = new URLSearchParams(searchParams);
    let touched = false;
    for (const key of ["diff", "comment", "reply"]) {
      if (params.has(key)) {
        params.delete(key);
        touched = true;
      }
    }
    if (touched) setSearchParams(params, { replace: true });
  }, [closeDiff, searchParams, setSearchParams]);

  const onCounterpart = useCallback(
    (comment: WorkbenchComment) => {
      const label = counterpartLabel(comment);
      if (!label) return;
      const kind = label.toLowerCase() as Kind;
      if (!shown.includes(kind)) {
        setShown([...shown, kind]);
        toast.show(`Showing ${kind}`);
        return;
      }
      scrollPaneIntoView(kind);
    },
    [shown, setShown, toast, scrollPaneIntoView],
  );

  // Re-fetch the unit's comments after any authoring write, so the gutters +
  // the docz-pane highlight spans re-derive from the SERVER truth (the write is
  // persisted, not a local illusion — it survives a reload).
  const reloadComments = useCallback(async () => {
    if (!unit) return;
    try {
      setComments(await articleApi.listComments(unit.id));
    } catch {
      // A read failure leaves the panes readable; the gutters keep prior state.
    }
  }, [unit]);

  const cancelPopover = useCallback(() => {
    setPopover(null);
    clearPending();
    window.getSelection()?.removeAllRanges();
  }, [clearPending]);

  // §2.5 CREATE: persist an anchored thread root on the PANE's document (so the
  // backend stamps its `home_resource` from that document's resource_role → it
  // lands in the pane's gutter and its selection re-anchors as a highlight span).
  const createRoot = useCallback(
    async (draft: {
      body: string;
      body_format: string;
      type: CommentType;
      mentions: number[];
      visibility?: import("../api/types").CommentVisibility;
      shared_with?: number[];
    }) => {
      if (!popover || !unit) return;
      const docId = unit.resources[popover.role].doc_id;
      try {
        const created = await articleApi.createComment(docId, {
          ...popover.draft,
          body: draft.body,
          body_format: draft.body_format,
          type: draft.type,
          mentions: draft.mentions,
          visibility: draft.visibility,
          shared_with: draft.shared_with,
        });
        // A comment authored on a LIVE EDITING SURFACE has no server-rendered read
        // pane to re-anchor against, so hand the SAME durable selector straight
        // back to the surface with the id the server just minted: the surface
        // registers the anchor and paints its highlight band. Read-mode comments
        // keep re-anchoring through `useDoczCanvas` as before.
        if (popover.selector) {
          const ref = popover.role === "brief" ? briefEditorRef : articleEditorRef;
          ref.current?.resolveAnchor(popover.selector, String(created.id));
        }
        setPopover(null);
        clearPending();
        window.getSelection()?.removeAllRanges();
        await reloadComments();
        toast.show("Comment added");
      } catch (err) {
        const offline =
          !getRestOutbox().isOnline || (err instanceof ApiError && err.status === 0);
        if (offline) {
          const tempId = mintTempId();
          const create: CommentCreate = {
            ...popover.draft,
            body: draft.body,
            body_format: draft.body_format,
            type: draft.type,
            mentions: draft.mentions,
            visibility: draft.visibility,
            shared_with: draft.shared_with,
          };
          void getRestOutbox().enqueueCreateRoot(tempId, docId, create);
          setPopover(null);
          clearPending();
          window.getSelection()?.removeAllRanges();
          toast.show("You're offline — comment queued, will sync");
        } else {
          toast.show(
            err instanceof ApiError && err.message
              ? err.message
              : "Couldn't save the comment",
          );
        }
      }
    },
    [popover, unit, reloadComments, clearPending, toast],
  );

  // REPLY: persist through the real endpoint (W1 only mutated local state, so a
  // reply vanished on reload — the data-loss illusion).
  const onReply = useCallback(
    async (parentId: number, draft: { body: string; body_format: string; mentions: number[] }) => {
      try {
        await articleApi.replyComment(parentId, draft);
        await reloadComments();
        toast.show("Reply posted");
      } catch {
        toast.show("Couldn't post the reply");
      }
    },
    [reloadComments, toast],
  );

  // STATUS: persist through the real endpoint (resolve/pin/act-on survives reload).
  const onStatus = useCallback(
    async (commentId: number, status: CommentStatus) => {
      try {
        await articleApi.setCommentStatus(commentId, status);
        await reloadComments();
      } catch {
        toast.show("Couldn't update status");
      }
    },
    [reloadComments, toast],
  );

  // comment-visibility (§6A.7): change a thread root's scope via the card pill.
  const onVisibility = useCallback(
    async (commentId: number, body: VisibilityChange) => {
      try {
        await articleApi.setCommentVisibility(commentId, body);
        await reloadComments();
        toast.show("Visibility updated");
      } catch {
        toast.show("Couldn't update visibility");
      }
    },
    [reloadComments, toast],
  );

  // review-notes §3: author edits their own note body (author-only, persisted
  // through the edit endpoint). Reconcile via a reload so the gutters repaint.
  const onEdit = useCallback(
    async (commentId: number, draft: { body: string; body_format: string }) => {
      // Optimistic body patch + durable outbox (online: immediate PATCH with a
      // stale-CSRF-token refresh; offline: queue + replay). Reconcile via reload.
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? { ...c, body: draft.body, body_format: draft.body_format }
            : c,
        ),
      );
      void getRestOutbox().enqueueEdit(commentId, draft.body, draft.body_format);
      if (!getRestOutbox().isOnline) {
        toast.show("You're offline — edit queued, will sync");
      }
    },
    [toast],
  );

  // comment-delete: the author deletes their own note. Deliberately NOT
  // optimistic — a delete's outcome is not knowable client-side. The server
  // TOMBSTONES: a note with no replies vanishes from the list, while one with
  // living replies comes back as a content-free placeholder that must keep its
  // place in the thread. Guessing which of those happened would show the wrong
  // thing half the time, so the authoritative reload IS the reconcile.
  const onDeleteComment = useCallback(
    async (commentId: number) => {
      try {
        await articleApi.deleteComment(commentId);
        await reloadComments();
      } catch (err) {
        // Surface the server's own refusal. A 403 here means the viewer is not
        // the author (the affordance should not have been shown) and a generic
        // "couldn't delete" would send them retrying a write that will never
        // succeed.
        toast.show(
          err instanceof ApiError && err.status === 403
            ? "Only the author can delete this comment"
            : "Couldn't delete that comment",
        );
      }
    },
    [reloadComments, toast],
  );

  // comment-version ribbon + switch: the per-pane version slices the comment
  // cards resolve against. `ticks` and `viewVer` are ALREADY the timeline's own
  // state — the cards read exactly what the timeline renders, so a note's `vX`
  // label can never disagree with the tick it points at.
  const displayedVersionByRole = useMemo(
    () => ({
      brief: viewVer.brief?.versionId ?? null,
      article: viewVer.article?.versionId ?? null,
      research: viewVer.research?.versionId ?? null,
    }),
    [viewVer],
  );
  // "Switch" on a comment's version popup routes into the SAME `onViewVersion`
  // the timeline tick click uses. One version-switch code path in the product;
  // this is a caller of it, not a second implementation.
  const onSwitchCommentVersion = useCallback(
    (role: Kind, versionId: string) => {
      void onViewVersion(role, versionId);
    },
    [onViewVersion],
  );

  // review-notes §4: toggle the viewer's emoji reaction on a diff-note. Persist
  // through the toggle endpoint then reload so both gutters repaint; on failure
  // reload to roll back any optimistic chip.
  const onToggleReaction = useCallback(
    (commentId: number, emoji: string, add: boolean) => {
      if (!user) return;
      const viewer = { id: Number(user.id), name: user.name };
      // Optimistic chip flip + durable outbox: applies now online, QUEUES offline
      // (no hard error). Reconcile repaints both gutters via reload.
      setComments((prev) => toggleOwnReaction(prev, commentId, emoji, add, viewer));
      void getRestOutbox().enqueueReaction(Number(user.id), commentId, emoji, add);
    },
    [user],
  );

  // Fold REST-outbox drains back into the workbench: a reload repaints both
  // gutters with server truth; a genuine rejection rolls the optimistic patch
  // back (the reload) and surfaces the specific reason.
  useEffect(() => {
    const off = getRestOutbox().subscribe((e) => {
      void reloadComments();
      if (e.kind === "rejected") {
        toast.show(
          e.message ||
            (e.table === "reactions"
              ? "Couldn't update reaction"
              : e.table === "comment_edit"
                ? "Couldn't save the edit"
                : "Couldn't save the comment"),
        );
      }
    });
    return off;
  }, [reloadComments, toast]);

  // Entering edit on a docz pane (brief/article) lazily builds the WYSIWYG
  // handle from the SERVER-EMITTED transport (Zig owns the IR — no client
  // fromHtml). research edits inline (markdown, no handle). Read-mode toggle is
  // instant. (design §1.9 · spec F3)
  //
  // RETURNS whether the pane actually moved. A bare `catch {}` here is how the
  // whole "write it directly in the editor" flow shipped broken: building the
  // handle can throw (it did — an unsaved document's `base_version_id` is `""`,
  // which the docz document schema rejects), and because the failure was
  // swallowed with no reason and no signal, Edit presented as a control that did
  // nothing and the `?edit=` deep-link consumed its own URL param on the way
  // past. The reason is now logged with the error, and the caller can tell
  // "entered" from "refused".
  const toggleEdit = useCallback(
    async (kind: Kind): Promise<boolean> => {
      const entering = !editing[kind];
      if (
        entering &&
        (kind === "brief" || kind === "article") &&
        unit &&
        !handles[kind]
      ) {
        try {
          const transport = await articleApi.getDoczDocument(
            unit.resources[kind].doc_id,
          );
          const handle = await buildDoczHandle(transport);
          setHandles((h) => ({ ...h, [kind]: handle }));
        } catch (err) {
          console.error(
            `[ArticleWorkbench] could not open the ${kind} editor for document ` +
              `${unit?.resources[kind].doc_id}:`,
            err,
          );
          toast.show("Couldn't open the editor for this pane");
          return false;
        }
      }
      setEditing((prev) => ({ ...prev, [kind]: !prev[kind] }));
      return true;
    },
    [editing, handles, unit, toast],
  );

  // `?edit=<kind>` — enter edit on that pane ONCE, as soon as the unit is loaded.
  // This is what makes "write it directly in the editor" a flow rather than a
  // suggestion: `<NewArticleDialog>` creates a project with an EMPTY brief and
  // navigates here, and without this the user lands on a read view of a blank
  // document and has to find the Edit button themselves.
  //
  // Fires at most once per mount (`autoEditDone`) — `toggleEdit`'s identity
  // changes on every `editing` update, so an unguarded effect would toggle the
  // pane straight back out of edit.
  //
  // ── IT WAITS FOR `detailLoaded`, AND THAT IS LOAD-BEARING ───────────────────
  // The review-data effect above ends with
  // `setEditing({brief:false, article:false, research:false})` — "a fresh unit
  // starts read-only". It is SIX network requests long. This effect used to fire
  // on `unit` alone, so the two raced: enter edit, and a moment later the reset
  // landed and put the pane back in read. MEASURED in a real browser on
  // 2026-07-28 against a brand-new project: the URL came back
  // `?show=brief&canvasEdit=1` — the param CONSUMED — with no save control and
  // ZERO canvases and ZERO contenteditables in the pane; while an earlier run of
  // the identical scenario, where the doc fetch happened to lose the race, had
  // opened the editor correctly (5 canvases, height 1123). An intermittent
  // "the button does nothing" is still "the button does nothing".
  //
  // Waiting is the whole fix: the reset has already run by the time the deep
  // link is honoured, so the two no longer contend and the outcome no longer
  // depends on which of seven requests returns first.
  //
  // THE PARAM IS CONSUMED ONLY ON SUCCESS. It used to be stripped up-front,
  // before the await — so when entering edit failed, the intent was destroyed
  // along with it: the URL no longer said what the user asked for, a reload
  // could not retry, and the surface simply sat there in read mode. That is the
  // precise shape that was measured on the live release ("`?edit=` consumed, the
  // pane stays READ-ONLY"). Stripping AFTER a confirmed enter keeps the two
  // halves honest — the param is gone exactly when the editor is open, so a
  // reload still cannot re-enter edit over work in progress.
  const autoEditDone = useRef(false);
  useEffect(() => {
    if (autoEditDone.current || !unit || !detailLoaded) return;
    const raw = searchParams.get("edit");
    if (raw !== "brief" && raw !== "research" && raw !== "article") return;
    autoEditDone.current = true;
    void (async () => {
      if (!(await toggleEdit(raw))) return;
      const params = new URLSearchParams(searchParams);
      params.delete("edit");
      setSearchParams(params, { replace: true });
    })();
  }, [unit, detailLoaded, searchParams, setSearchParams, toggleEdit]);

  // Whole-resource save-back (spec D1/F3): brief/article commit the WYSIWYG
  // editor's `toHtml()`; research commits its markdown. The server re-carves +
  // advances only changed per-section streams; we refetch the resource ticks so
  // the new head tick appears, then drop back to read mode.
  const onSave = useCallback(
    async (kind: Kind) => {
      if (!unit) return;
      setSaving((s) => ({ ...s, [kind]: true }));
      try {
        let payload: ResourceSavePayload;
        if (kind === "research") {
          payload = { body_md: researchMd };
        } else {
          const ref = kind === "brief" ? briefEditorRef : articleEditorRef;
          const html = ref.current?.toHtml();
          if (html == null) {
            toast.show("Nothing to save yet");
            return;
          }
          // ── THE SAVE REFUSAL (P0 2026-07-28) ────────────────────────────────
          // A save that would DESTROY structure the document still holds does not
          // get written. On 2026-07-28 a serializer defect replaced all 8 real
          // `<table>` elements of a delivered client review with tab-separated
          // paragraphs, and a sibling document lost its `<li>` content — silently,
          // with a 200 response. Six client documents were recovered from source
          // `.docx` that day.
          //
          // The predicate is the surface's own `structuralLossOnSave()`: the live
          // model's structural census versus the census of the exact bytes about to
          // be posted. It cannot fire on an INTENDED deletion (the model already
          // lost the table), only on a serializer that drops what the model kept.
          // The same principle the server applies in `save_identity.zig`
          // `refusesDestructiveClear` (409 when a save would empty a non-empty
          // document), extended from emptying to structural destruction.
          //
          // This is the CLIENT half and it is advisory: it fails fast where the
          // reviewer can still act, and it can be bypassed by a stale bundle. The
          // authoritative half belongs server-side, next to `refusesDestructiveClear`.
          const loss = ref.current?.structuralLossOnSave?.() ?? [];
          if (loss.length > 0) {
            const detail = loss.map((l) => `${l.kind} ${l.before}→${l.after}`).join(", ");
            console.error(
              `[save] REFUSED — serializing this document would destroy structure it ` +
                `still holds (${detail}). Nothing was written. This is a serializer ` +
                `defect, not an edit: the in-memory document still has the content.`,
            );
            toast.show(`Save blocked: this would delete ${detail}. Nothing was saved.`);
            return;
          }
          payload = { rendered_html: html };
        }
        await articleApi.saveResource(unit.id, kind, payload);
        // ADOPT THE ACCEPTED BODY (P3-FIX-4B). The save resolved, so these bytes
        // ARE the document now. Without this the pane drops back to read mode
        // and repaints the body it loaded BEFORE the edit — the reviewer's own
        // save silently vanishing from the surface they review on. `research`
        // reads from `researchMd`, which is already the posted text.
        if (payload.rendered_html != null && kind !== "research") {
          const accepted = payload.rendered_html;
          setSavedBody((b) => ({ ...b, [kind]: accepted }));
        }
        // …and make the canvas reader re-fetch: it owns its own transport under
        // a docId that did not change, so nothing else would tell it to.
        setReadRev((r) => r + 1);
        const nextTicks = await articleApi.resourceVersions(unit.id, kind);
        setTicks((t) => ({ ...t, [kind]: nextTicks }));
        setDirty((d) => ({ ...d, [kind]: false }));
        setEditing((e) => ({ ...e, [kind]: false }));
        toast.show(`Saved ${kind}`);
      } catch {
        toast.show(`Couldn't save ${kind}`);
      } finally {
        setSaving((s) => ({ ...s, [kind]: false }));
      }
    },
    [unit, researchMd, toast],
  );

  const onResearchChange = useCallback((md: string) => {
    setResearchMd(md);
    setDirty((d) => ({ ...d, research: true }));
  }, []);

  // W3 (§1.6): EXPLICIT human re-pin of a stale link, then refetch the link set
  // so the staleness indicator clears.
  const onRepin = useCallback(
    async (linkId: number) => {
      if (!unit) return;
      try {
        await articleApi.repinLink(linkId);
        const next = await articleApi.listLinks(unit.id);
        setLinks(next);
        toast.show("Re-pinned to the current head");
      } catch {
        toast.show("Couldn't re-pin this link");
      }
    },
    [unit, toast],
  );

  // W3 (§2.5): a link's ↔ chip scrolls the OTHER pane into view (revealing it if
  // hidden), so the reviewer jumps from an article section to its brief/research.
  const onLinkCounterpart = useCallback(
    (role: ResourceRole) => {
      const kind = role as Kind;
      if (!shown.includes(kind)) {
        setShown([...shown, kind]);
        toast.show(`Showing ${kind}`);
        return;
      }
      scrollPaneIntoView(kind);
    },
    [shown, setShown, toast, scrollPaneIntoView],
  );

  // Edit-mode "New comment" — open the SAME composer a read-mode text selection
  // opens (setPopover), anchored to the LIVE EDITING SURFACE's own selection.
  //
  // THE SURFACE IS ASKED; THE DOM IS NEVER PROBED. This used to read
  // `window.getSelection()` and require the range to sit inside
  // `[data-testid="elaborate-editor"]`. On the PAGINATED canvas — the default
  // editing surface since 2026-07-27 — neither exists: the document is a
  // `<canvas>` and the reviewer's selection is the WASM core's `{anchor,focus}`
  // IR pair, so the DOM selection is permanently collapsed. Every click of the
  // Review ▸ New comment button therefore early-returned and did NOTHING, while
  // the button rendered fully enabled (its `requires: 'comment'` gate reads
  // `Boolean(host.addComment)`). One `handle.buildSelector()` — no Range, meaning
  // "your own live selection" — answers for BOTH surfaces, which is the same
  // reason `hasSelection()` lives on the handle.
  //
  // THE POPUP OPENS FOR ANY SELECTION (owner requirement, 2026-07-29: "the comment
  // popup must show regardless of the selection"). The toast is kept for the ONE
  // honest case — no selection at all — and for nothing else. A range that cannot
  // be anchored durably (a table, a list, a blank cell) still opens the composer
  // and DISCLOSES why in it, because replacing the popover with a toast is how the
  // reviewer ends up being told to select text they had already selected.
  const openEditorComment = useCallback(
    (role: "brief" | "article") => {
      const ref = role === "brief" ? briefEditorRef : articleEditorRef;
      const target = commentTargetFromSurface(ref.current);
      if (!target) {
        toast.show("Select the text you want to comment on first");
        return;
      }
      const rect = target.rect;
      setPopover({
        x: rect ? rect.left + rect.width / 2 : window.innerWidth / 2,
        y: rect ? rect.bottom + 8 : 120,
        draft: target.draft,
        selector: target.selector ?? undefined,
        role,
        notice: target.anchorable ? undefined : (target.reason ?? undefined),
      });
    },
    [toast],
  );

  // "Comment on this section" (the handle popover) — open the SAME composer the
  // text-selection path opens (§2.5), with a durable anchor synthesised at the
  // section's heading block (`anchor_key` + the heading text as the quote). The
  // created root inherits the pane's document → its gutter, exactly like a
  // selection-authored comment.
  const onCommentSection = useCallback(
    (role: "brief" | "article", section: Section) => {
      const canvas = role === "brief" ? briefCanvas : articleCanvas;
      // The section's heading block, in the one coordinate a canvas has. This
      // was `querySelector('[data-block-id=…]').getBoundingClientRect()` on the
      // DOM copy; there is no such element now, so the engine is asked.
      const rect = canvas.rectForBlockRef(section.anchor_key) ?? undefined;
      const draft: AnnotationDraft = {
        block_ref: section.anchor_key,
        quote: section.heading,
        prefix: "",
        suffix: "",
        start: 0,
        end: section.heading.length,
        quote_hash: hashQuote(section.heading),
      };
      setPopover({
        x: rect ? rect.left + rect.width / 2 : window.innerWidth / 2,
        y: rect ? rect.bottom + 8 : 120,
        draft,
        role,
      });
    },
    [briefCanvas, articleCanvas],
  );

  // ── TOOLBAR HOST IDENTITY ───────────────────────────────────────────────────
  // `canvasFor` below is a PLAIN FUNCTION called during render, so a host object
  // built inside it is a brand-new identity on every single render. That defeats
  // `<EditorToolbarMount>`'s `bridge` memo (keyed on `[editorHandle, host, …]`),
  // which then rebuilds the bridge and re-renders the whole `<ToolbarShell>` on
  // every keystroke, every selection tick and every SSE arrival. Built here it
  // moves only when an action it forwards to actually changes.
  const makeToolbarHost = useCallback(
    (kind: "brief" | "article"): Partial<EditorHostBridge> => {
      const ref = kind === "brief" ? briefEditorRef : articleEditorRef;
      const canvas = kind === "brief" ? briefCanvas : articleCanvas;
      const docId = unit?.resources[kind].doc_id;
      return {
        save: () => void onSave(kind),
        print: () => window.print(),
        export: () => {
          if (docId == null) return;
          window.open(api.exportUrl(docId, "pdf"), "_blank", "noopener");
        },
        openSettings: () => navigate(`/w/${slug}/settings`),
        // "New comment": in EDIT mode the selection lives in the mounted editor,
        // so open the composer from the editor's selection; in read mode ask the
        // read canvas to re-emit its live selection.
        addComment: () => {
          if (editing[kind]) openEditorComment(kind);
          else canvas.requestComment();
        },
        undo: () => void ref.current?.undo(),
        redo: () => void ref.current?.redo(),
        canUndo: () => ref.current?.canUndo() ?? false,
        canRedo: () => ref.current?.canRedo() ?? false,
        cut: () => ref.current?.cut(),
        copy: () => ref.current?.copy(),
        // The BUTTON paste can genuinely fail where ⌘V cannot (Safari's
        // ClipboardItem is text-only; Chrome may reject the read with
        // NotAllowedError). Surface the honest outcome, never a silent no-op.
        paste: () => {
          const handle = ref.current;
          if (!handle) return;
          void handle.pasteFromSystem().then((result) => {
            if (result === "ok") return;
            toast.show(
              result === "denied"
                ? "Clipboard access was blocked — press ⌘V to paste"
                : "This browser can't paste from a button — press ⌘V instead",
            );
          });
        },
        canPaste: () => ref.current?.canPaste() ?? false,
        formatPainter: () => void ref.current?.formatPainter(),
      };
    },
    [unit, slug, editing, onSave, navigate, toast, openEditorComment, briefCanvas, articleCanvas],
  );
  const toolbarHosts = useMemo(
    () => ({ brief: makeToolbarHost("brief"), article: makeToolbarHost("article") }),
    [makeToolbarHost],
  );

  if (loading) {
    return (
      <div style={{ paddingTop: 80 }}>
        <Spinner label="Opening article unit…" />
      </div>
    );
  }
  if (error || !unit) {
    return (
      <div className="page" data-testid="workbench-error">
        <EmptyState
          icon={<FileText size={30} strokeWidth={1.5} />}
          title="This article unit isn’t here"
          body="It may have been moved or deleted, or the link is off. Head back to the workspace to pick another."
          action={
            <Button
              variant="secondary"
              icon={<ArrowLeft size={15} strokeWidth={1.5} />}
              onClick={() => navigate(`/w/${slug}`)}
              data-testid="workbench-error-back"
            >
              Back to the workspace
            </Button>
          }
        />
      </div>
    );
  }

  const canvasFor = (kind: "brief" | "article") => {
    const c = kind === "brief" ? briefCanvas : articleCanvas;
    const ref = kind === "brief" ? briefEditorRef : articleEditorRef;
    const resource = unit.resources[kind];
    // Host actions the toolbar shell needs the workbench to own — built ONCE in
    // `makeToolbarHost` above, never a per-render literal. Structural inserts
    // (block / page-break) are wired to the editor handle inside the mount.
    // History + clipboard + painter are REAL: the editor owns the bounded docz-IR
    // snapshot ring, the three-flavour clipboard and the format painter, so the
    // workbench forwards to the live handle and reports the LIVE stack depth /
    // browser permission (never `Boolean(cb)` — a constant that could not disable
    // at the ends of the stack). `find` remains a genuine gap → its command stays
    // honestly disabled.
    //
    // DIRTY/SAVE: undo and redo mutate the document through the SAME controller
    // mutation path a keystroke does, so they emit `onChange` → `DoczCanvas` bumps
    // its change tick → `onEditorChange` marks the pane dirty and refreshes the
    // word count. Save keeps posting `editorRef.toHtml()`, which serializes the
    // restored IR. COMMENT ANCHORS: placing a comment is a review act that CLEARS
    // both stacks in the editor (`historyBarrier`), so undo can never rewind past a
    // comment and orphan its server-side anchor on the next save.
    const toolbarHost = toolbarHosts[kind];
    return (
      <DoczCanvas
        editable={editing[kind]}
        handle={handles[kind]}
        editorRef={ref}
        onEditorChange={() => {
          setDirty((d) => ({ ...d, [kind]: true }));
          // Bump the live word-count tick (the bottom bar re-reads toHtml()).
          setEditTick((t) => t + 1);
        }}
        surfaceClassName="wb-paper"
        canvasClassName="ela-doc"
        testId={`${kind}-canvas`}
        toolbarContainer={dockEl[kind]}
        toolbarConfig={toolbarCfg}
        toolbarHost={toolbarHost}
        // A GESTURE THE CANVAS REFUSED, spoken out loud. Measured 2026-07-28: a
        // click inside a table cell painted zero caret pixels, moved the selection
        // nowhere and reported NOTHING anywhere, while the caret stayed off-screen
        // on another page and the next keystrokes corrupted it. The engine now
        // refuses loudly and scrolls the caret back into view; this is where it
        // reaches the reviewer.
        onRefusal={(message) => toast.show(message)}
        // ── THE READ SURFACE ─────────────────────────────────────────────────
        // The only one. The DOM twin and the `readCanvas` flag that selected
        // between them are both deleted; these props ARE read mode.
        readDocId={resource.doc_id}
        readComments={kind === "brief" ? briefHomed : articleHomed}
        readTitle={resource.title}
        readLang={unit.lang}
        onReadCanvasSelect={kind === "brief" ? onSelectBrief : onSelectArticle}
        onReadCanvasSelectComment={revealCommentCard}
        readCanvasRef={c.ref}
        onReadCanvasAnchorStates={c.onAnchorStates}
        onReadCanvasAnchorPoints={c.onAnchorPoints}
        onReadCanvasHoverComment={setLinkedCommentId}
        readRevision={readRev}
        overlay={
          editing[kind] ? null : (
            <SectionHandleLayer
              kind={kind}
              sections={resource.sections}
              surfaceRef={c.ref}
              paintKey={bodyFor(kind) ?? ""}
              active={shown.includes(kind) && !editing[kind]}
              links={links}
              onCompareSection={(section) =>
                void compare.openSectionTray(kind, section)
              }
              onRepin={onRepin}
              onLinkCounterpart={onLinkCounterpart}
              onCommentSection={(section) => onCommentSection(kind, section)}
            />
          )
        }
      />
    );
  };

  // Live reader-visible word count `n` for a docz pane: from the live editor
  // (edit mode) or the stored rendered HTML (read mode). `editTick` (bumped on
  // editor change) keeps the edit-mode count live. research has no bar → no count.
  const wordCountFor = (kind: "brief" | "article"): { n: number; chars: number } => {
    void editTick; // read so edit-mode counts recompute on each editor change
    // The word count reads the SAME body the surface paints — otherwise a save
    // would leave the bottom bar counting the pre-edit document.
    let html = viewVer[kind]?.html ?? bodyFor(kind) ?? "";
    if (editing[kind]) {
      const ref = kind === "brief" ? briefEditorRef : articleEditorRef;
      const live = ref.current?.toHtml();
      if (live != null) html = live;
    }
    return { n: countWordsInHtml(html, unit.lang), chars: stripHtml(html).length };
  };

  const renderPane = (kind: Kind) => {
    const resource = unit.resources[kind];
    // The bottom bar (design §3) — brief/article only; research gets NONE.
    const footer =
      kind === "research" ? undefined : (
        (() => {
          const wc = wordCountFor(kind);
          return (
            <PaneBottomBar
              kind={kind}
              docId={resource.doc_id}
              title={resource.title}
              wordCount={wc.n}
              charCount={wc.chars}
              sectionCount={resource.sections.length}
              targetWords={kind === "article" ? targetWords : undefined}
              lang={unit.lang}
              onTargetChange={kind === "article" ? setTargetWords : undefined}
            />
          );
        })()
      );
    // The pane header chip reflects the SHOWN version: the head by default
    // (its real seq from the timeline ticks — the aggregate resource.seq is a
    // placeholder of 1), or the past snapshot the reviewer opened.
    const list = ticks[kind];
    const headTick = list.find((t) => t.isHead) ?? list[list.length - 1];
    const vv = viewVer[kind];
    const displaySeq = vv ? vv.seq : (headTick?.seq ?? resource.seq);
    const displayHash = vv ? vv.hash : resource.content_hash;
    const header = (
      <ResourceHeader
        resource={resource}
        editable={editing[kind]}
        canEdit={true}
        versionSeq={displaySeq}
        versionHash={displayHash}
        viewingPast={vv != null}
        onReturnToHead={() => returnToHead(kind)}
        onToggleEdit={() => toggleEdit(kind)}
        onCompare={() => compare.openResourceTray(kind)}
        onSave={() => onSave(kind)}
        dirty={dirty[kind]}
        saving={saving[kind]}
      />
    );
    // The editor's formatting toolbar portals into this dock (edit mode, docz
    // panes only) so it renders as a second row of the sticky header chrome.
    const toolbarDock =
      (kind === "brief" || kind === "article") && editing[kind] ? (
        <div
          className="wb-toolbar-dock"
          data-kind={kind}
          data-testid={`toolbar-dock-${kind}`}
          ref={kind === "brief" ? setBriefDock : setArticleDock}
        />
      ) : null;
    // THE PER-TICK MENU. One action today; the shape is the lib's `TickAction`
    // so a second one is a list entry, not another interaction to design.
    //
    // Reverting to the version you are already ON is a no-op, so it is offered
    // DISABLED WITH ITS REASON rather than hidden: a reader who right-clicks the
    // head looking for revert must learn why it is unavailable, not conclude the
    // feature is missing or that they lack permission.
    //
    // Permission is NOT pre-judged here. `restore_version` is a server-side
    // capability and this screen holds no capability data; inventing a
    // client-side predicate would be a parallel permission table that drifts
    // from the matrix. The server decides and a 403 is surfaced verbatim.
    const tickActions: readonly TickAction[] = [
      {
        id: "revert",
        label: "Revert to this version",
        tone: "prominent",
        disabledReason: (t) =>
          t.isHead ? "this is already the current version" : null,
        onSelect: (versionId) => {
          const t = ticks[kind].find((x) => x.versionId === versionId);
          setRevertReport(null);
          setRevertAsk({
            kind,
            versionId,
            label: t?.label ?? `version ${versionId}`,
          });
        },
      },
    ];
    return (
      <ResourcePane
        kind={kind}
        tickActions={tickActions}
        versions={ticks[kind]}
        selectedA={compare.pins[kind]?.a}
        selectedB={compare.pins[kind]?.b}
        // Which tick is `aria-current` — the version the pane is PAINTING, not the
        // newest one. `vv` is null on the live head, and `TimelineBar` reads that as
        // "head is current", so the accent follows the reviewer instead of the
        // document's end.
        currentVersionId={vv?.versionId}
        onView={(versionId) => void onViewVersion(kind, versionId)}
        onSetA={(versionId) => compare.setResourceA(kind, versionId)}
        onSetB={(versionId) => compare.setResourceB(kind, versionId)}
        header={header}
        toolbarDock={toolbarDock}
        footer={footer}
      >
        {kind === "research" ? (
          <ResearchColumn
            resource={resource}
            editable={editing.research}
            value={researchMd}
            onChange={onResearchChange}
            onArticleChip={() => {
              if (!shown.includes("article")) setShown([...shown, "article"]);
              else scrollPaneIntoView("article");
            }}
          />
        ) : vv ? (
          // THE PAST REVISION, on the canvas engine (owner decision D-E: no DOM
          // renders any docz IR anywhere, version history included). This was a
          // `dangerouslySetInnerHTML` of `vv.html`; a reviewer comparing a
          // revision against the head has to be looking at the SAME renderer, or
          // "what changed" silently includes the renderer. `vv.html` survives for
          // the bottom bar's word count only — counting words in markup is not
          // painting it.
          //
          // ── THE PAPER UNDER IT IS A DESK, AND HAD TO SAY SO ────────────────
          // Owner 2026-07-31, verbatim: *"why only the HEAD version has
          // `data-layout-mode="paginated"`?!"* Because THIS mount is not
          // `<DoczCanvas>`. It is a hand-written `.wb-paper` in a sibling branch,
          // so the mode `<DoczCanvas>` derives never reached it and the revision
          // got the CONTINUOUS manuscript chrome (44px/52px + border + shadow)
          // wrapped around a real A4 canvas — the exact 106px defect that was
          // fixed for read-vs-edit on 2026-07-29, reappearing at head-vs-history
          // because the fix lived inside the component rather than above it.
          //
          // The renderer was never the problem and still is not: `<VersionCanvas>`
          // composes the same `<ReadCanvas>` the head paints through, so a
          // reviewer HAS been comparing one renderer. What differed was the BOX
          // around it. `paintsPaginatedCanvas` is unconditionally `true` here
          // because `<VersionCanvas>` has no continuous mode to fall back to.
          <div
            className="wb-paper"
            data-layout-mode={paperLayoutMode(true)}
            data-testid={`resource-version-view-${kind}`}
          >
            <VersionCanvas
              docId={resource.doc_id}
              versionId={vv.versionNum}
              title={`${resource.title} — ${vv.label} (read-only)`}
              lang={unit.lang}
              testId={`resource-version-canvas-${kind}`}
            />
          </div>
        ) : (
          canvasFor(kind)
        )}
      </ResourcePane>
    );
  };

  const renderGutter = (pairing: string) => (
    <CommentGutter
      pairing={pairing}
      comments={comments}
      members={members}
      anchorStates={anchorStates}
      documentOrder={documentOrder}
      onReply={onReply}
      onStatus={onStatus}
      onFocusAnchor={focusComment}
      onCounterpart={onCounterpart}
      slug={slug}
      unitId={unitId}
      linkedCommentId={linkedCommentId}
      onHoverCard={onHoverCard}
      viewerId={user ? Number(user.id) : undefined}
      viewerIsStaff={user?.is_staff}
      onToggleReaction={onToggleReaction}
      onVisibility={onVisibility}
      onEdit={onEdit}
      revealCommentId={commentParam}
      flashCommentId={flashCommentId}
      onDelete={onDeleteComment}
      versionTicksByRole={ticks}
      displayedVersionByRole={displayedVersionByRole}
      onSwitchVersion={onSwitchCommentVersion}
    />
  );

  return (
    <div className="wb-root" data-testid="article-workbench">
      <WorkbenchBar
        slug={slug}
        title={unit.title}
        status={unit.status}
        hash={unit.resources.article.content_hash}
        shown={shown}
        onShownChange={setShown}
        onCopyHash={() => toast.show("Hash copied")}
      />
      <div className="wb-stage-wrap" ref={stageRef}>
        {/* STAGE TRACK WIDTHS — owner 2026-07-31, verbatim: "`.nk-review-workbench`
            — `grid-template-columns: 830px 680px`". That value IS this pair: the
            default shown-set is Article ONLY (`workbenchLayout.DEFAULT_SHOWN`),
            and `stageColumns` solves a solo pane as `<paneTrack> <gutterTrack>`.

            ⚠️ It is passed as PROPS, not written as CSS, and that is not a style
            choice. `<WorkbenchStage>` writes the solved track list as an INLINE
            `style={{ gridTemplateColumns }}` (web-review-workbench), so a
            `.nk-review-workbench { grid-template-columns: … }` rule in this app
            would lose the cascade to it and could only win with `!important`.
            The lib already exposes `paneTrack`/`gutterTrack` as consumer knobs
            for exactly this — so the value reaches the grid through the seam
            that owns it, and the solver stays the single source of track math
            (RULE ZERO: we do not restate the track list).

            ⚠️ `paneTrack` is the CSS VAR, not the literal `830px`. `stageColumns`
            documents that a host may hand in a var for exactly this reason, and
            the reason here is that 830px is not this screen's number: it is the
            width EVERY document pane gets, and the OTHER document surface (the
            library viewer, styles/screens.css) has to agree with it or its
            sheets clip. Both now resolve `--ela-doc-surface-track`
            (theme/ela-theme.css), so there is one track in the product rather
            than one per screen. Writing `830px` here again is how they drift. */}
        <WorkbenchStage
          shown={shown}
          renderPane={renderPane}
          renderGutter={renderGutter}
          paneTrack="var(--ela-doc-surface-track)"
          gutterTrack="680px"
          testId="workbench-stage"
        />
      </div>
      {compare.trayView ? (
        <CompareTray
          title={compare.trayView.title}
          ticks={compare.trayView.ticks}
          selectedA={compare.trayView.selectedA}
          selectedB={compare.trayView.selectedB}
          onSetA={compare.setTrayA}
          onSetB={compare.setTrayB}
          onSwap={compare.swapTray}
          onDiff={() => void compare.runCompare()}
          onClose={compare.closeTray}
        />
      ) : null}
      {compare.compareModel ? (
        <SideBySideDiff
          model={compare.compareModel}
          onClose={onDiffClose}
          testId="review-diff"
          versions={compare.compareCtx?.ticks}
          valueA={compare.compareCtx?.aId}
          valueB={compare.compareCtx?.bId}
          onCompare={onDiffCompare}
          loading={compare.loading}
          permalink={diffPermalink}
          revealChangeHash={revealHash}
          noteAnchors={diffNoteAnchors}
          onNoteAnchorClick={(anchor) => focusComment(Number(anchor.commentId))}
          revealCommentId={revealNoteCommentId}
          onHunkSelect={(hunk) => setActiveChangeHash(hunk.changeHash)}
        />
      ) : null}
      {popover ? (
        <div
          className="anno-popover anno-popover--composer"
          data-testid="annotation-popover"
          data-pane={popover.role}
          style={{ left: popover.x, top: popover.y }}
        >
          {popover.notice ? (
            <p className="anno-popover__notice" data-testid="annotation-anchor-notice" role="note">
              {popover.notice}
            </p>
          ) : null}
          <CommentComposer
            members={members}
            autoFocus
            submitLabel="Comment"
            onCancel={cancelPopover}
            onSubmit={createRoot}
            onTypeChange={onComposerType}
          />
        </div>
      ) : null}
      {/* ── the revert confirmation + its account of what moved ──────────────
          Rendered at the workbench root rather than inside the pane so the
          dialog is not clipped by the pane's scrolling chrome, and so exactly
          ONE can be open across the three panes. */}
      {revertAsk ? (
        <div className="wb-revert-modal" data-testid="revert-confirm-modal">
          <RestoreConfirm
            versionId={Number(revertAsk.versionId) || 0}
            versionLabel={revertAsk.label}
            busy={reverting}
            onCancel={() => setRevertAsk(null)}
            onConfirm={() => void doRevert()}
          />
        </div>
      ) : null}
      {revertReport ? (
        <div className="wb-revert-modal" data-testid="revert-report-modal">
          <RestoreReportCard
            report={revertReport}
            onDismiss={() => setRevertReport(null)}
          />
        </div>
      ) : null}
    </div>
  );
}
